import React, { useEffect, useState } from "react";
import { Modal, Input, Button, Form, DatePicker, InputNumber } from "antd";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/tr_TR";
import "dayjs/locale/tr";
import utc from "dayjs/plugin/utc";
import {
    hasRole,
    isEmailSendDateDisabled,
    getEmailSendDisabledTime,
} from "../../utils";
dayjs.extend(utc);
interface ReportInvoiceModalProps {
    visible: boolean;
    onClose: () => void;
    onSubmit?: (data: {
        invoiceAmount: string;
        reportUrl: string;
        financialInfoUrl: string;
        invoiceDate: string;
        invoiceEmailSendAt?: string;
    }) => void;
    initialValues: {
        invoiceAmount: string;
        reportUrl: string;
        financialInfoUrl: string;
        invoiceDate: string;
        invoiceEmailSendAt?: string;
    };
    type: "edit" | "view";
}

const ReportInvoiceModal: React.FC<ReportInvoiceModalProps> = ({
    visible,
    onClose,
    onSubmit,
    initialValues,
    type,
}) => {
    const [form] = Form.useForm();
    const [isCentralUser, setIsCentralUser] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const isUserAdmin = hasRole('ADMIN');
        setIsAdmin(isUserAdmin);

        const isUserCentralUser = hasRole('CENTRAL_USER');
        setIsCentralUser(isUserCentralUser);
    }, [])

    useEffect(() => {
        setLoading(false);
    }, [visible])


    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                ...initialValues,
                invoiceDate: initialValues.invoiceDate
                    ? dayjs(initialValues.invoiceDate) // Tarihi dayjs formatına çevir
                    : null,
                invoiceEmailSendAt: initialValues.invoiceEmailSendAt
                    ? dayjs(initialValues.invoiceEmailSendAt)
                    : null,
            });
        }
    }, [visible, initialValues, form, type]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (onSubmit) {
                setLoading(true);
                const postData = {
                    invoiceAmount: values.invoiceAmount,
                    reportUrl: values.reportUrl,
                    financialInfoUrl: values.financialInfoUrl,
                    invoiceDate: dayjs(values.invoiceDate).add(3, 'hour').toISOString(),
                    invoiceEmailSendAt: values.invoiceEmailSendAt
                        ? dayjs(values.invoiceEmailSendAt).add(3, 'hour').toISOString()
                        : undefined,
                };
                onSubmit(postData);
            }
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    return (
        <Modal
            title={type === "edit" ? "Kontrolü Tamamla" : (isAdmin || isCentralUser) ? "Rapor ve Faturayı Görüntüle" : "Fatura Görüntüle"}
            visible={visible}
            onCancel={onClose}
            footer={
                type === "edit" && (
                    <div style={{ textAlign: "center" }}>
                        <Button loading={loading} type="primary" className="d-button width-auto dark-button" onClick={handleSubmit}>
                            Kaydet ve Gönder
                        </Button>
                    </div>
                )
            }
            centered
        >
            <Form form={form} layout="vertical">
                {/* Fatura Tutarı */}
                <Form.Item
                    label="Fatura Tutarı"
                    name="invoiceAmount"
                    rules={[
                        { required: true, message: 'Lütfen fatura tutarını giriniz.' },
                    ]}
                >
                    <InputNumber
                        placeholder="Fatura Tutarı"
                        min={0}
                        step={0.01}
                        className="d-input d-input-form"
                        precision={2}
                        style={{ width: "100%" }}
                        readOnly={type === "view"}

                        onKeyDown={(event) => {
                            if (event.key === ',') {
                                event.preventDefault();
                            }
                        }}
                    />
                </Form.Item>

                {/* Fatura Tarihi */}
                <Form.Item
                    label="Fatura Tarihi"
                    name="invoiceDate"
                    rules={[
                        { required: true, message: "Lütfen fatura tarihini seçiniz." },
                    ]}
                >
                    <DatePicker
                        locale={locale}
                        format="DD/MM/YYYY"
                        style={{ width: "100%" }}
                        placeholder="GG/AA/YYYY"
                        disabled={type === "view"}
                        value={initialValues.invoiceDate ? dayjs(initialValues.invoiceDate) : null}
                        onChange={(date) => {
                            if (type !== "view") {
                                form.setFieldsValue({ invoiceDate: date });
                            }
                        }}
                    />
                </Form.Item>

                {(isAdmin || isCentralUser) && (
                    <>
                        <Form.Item
                            label="Rapor"
                            name="reportUrl"
                            rules={[
                                { required: true, message: "Lütfen geçerli bir URL giriniz." },
                                { type: "url", message: "Geçerli bir URL giriniz." },
                            ]}
                        >
                            {type === "view" ? (
                                <a
                                    href={initialValues.reportUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Görüntüle
                                </a>
                            ) : (
                                <Input placeholder="Rapor URL" />
                            )}
                        </Form.Item>
                    </>
                )}
                {/* Mali Bilgiler URL */}
                <Form.Item
                    label="Mali Bilgiler"
                    name="financialInfoUrl"
                    rules={[
                        { required: true, message: "Lütfen geçerli bir URL giriniz." },
                        { type: "url", message: "Geçerli bir URL giriniz." },
                    ]}
                >
                    {type === "view" ? (
                        <a
                            href={initialValues.financialInfoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Görüntüle
                        </a>
                    ) : (
                        <Input placeholder="Mali Bilgiler URL" />
                    )}
                </Form.Item>
                {type === "edit" && (
                    <>
                        <p>
                            Dilerseniz gönderimi ileri bir tarih/saat için planlayabilirsiniz.
                            Tarih seçilmezse gönderim hemen gerçekleşir.
                        </p>
                        <Form.Item
                            label="E-posta Gönderim Tarihi (isteğe bağlı)"
                            name="invoiceEmailSendAt"
                        >
                            <DatePicker
                                locale={locale}
                                showTime={{ minuteStep: 5 }}
                                format="DD/MM/YYYY HH:mm"
                                style={{ width: "100%" }}
                                placeholder="GG/AA/YYYY SS:DD"
                                disabledDate={isEmailSendDateDisabled}
                                disabledTime={getEmailSendDisabledTime}
                            />
                        </Form.Item>
                    </>
                )}
                {type === "view" && initialValues.invoiceEmailSendAt && (
                    <Form.Item
                        label="E-posta Gönderim Tarihi"
                    >
                        <span>{dayjs(initialValues.invoiceEmailSendAt).locale("tr").format("DD/MM/YYYY HH:mm")}</span>
                    </Form.Item>
                )}
            </Form>
        </Modal >
    );
};

export default ReportInvoiceModal;
